import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { LineReveal } from "../components/RevealText";

const EASE = [0.76, 0, 0.24, 1];

const HERO_VIDEO_DESKTOP = "/assets/hero.mp4";
const HERO_VIDEO_MOBILE = "/assets/hero_mobile.mp4";
const MOBILE_QUERY = "(max-width: 767px), (orientation: portrait) and (max-width: 1024px)";

const isMobileInitial = () => {
  if (typeof window === "undefined") return false;
  return window.matchMedia(MOBILE_QUERY).matches;
};

export default function Home() {
  const heroRef = useRef(null);
  const videoRef = useRef(null);
  const [isMobile, setIsMobile] = useState(isMobileInitial);
  const videoSrc = isMobile ? HERO_VIDEO_MOBILE : HERO_VIDEO_DESKTOP;

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const update = () => setIsMobile(mq.matches);
    mq.addEventListener ? mq.addEventListener("change", update) : mq.addListener(update);
    return () => {
      mq.removeEventListener ? mq.removeEventListener("change", update) : mq.removeListener(update);
    };
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.load();
    v.play().catch(() => {});
  }, [videoSrc]);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  const videoScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const videoY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const logoY = useTransform(scrollYProgress, [0, 1], [0, -180]);
  const logoOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const heroTextY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  // Seamless loop: reset just before end to avoid rebuffer gap
  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    if (v.duration - v.currentTime < 0.08) {
      v.currentTime = 0;
      v.play().catch(() => {});
    }
  };

  const handleEnded = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    v.play().catch(() => {});
  };

  return (
    <main data-testid="home-page" className="bg-black text-white">
      {/* HERO */}
      <section
        ref={heroRef}
        className="relative h-[75vh] md:h-screen w-full overflow-hidden grain vignette"
        data-testid="hero-section"
      >
        {/* Video layer */}
        <motion.div
          style={{ scale: videoScale, y: videoY }}
          className="absolute inset-0"
        >
          <video
            ref={videoRef}
            key={videoSrc}
            data-testid="hero-video"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="w-full h-full object-cover slog-video bg-black"
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleEnded}
          >
            <source src={videoSrc} type="video/mp4" />
          </video>
        </motion.div>

        {/* Center logo */}
        <motion.div
          style={{ y: logoY, opacity: logoOpacity }}
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.4, ease: EASE }}
          className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none"
        >
          <img
            src="/assets/anron_logo_light.png"
            alt="Anron"
            data-testid="hero-logo"
            className="w-[70vw] max-w-[720px] md:w-[42vw] drop-shadow-[0_6px_40px_rgba(0,0,0,0.8)]"
          />
        </motion.div>

        {/* Bottom left - Company location */}
        <motion.div
          style={{ y: heroTextY }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.4 }}
          className="absolute bottom-8 left-4 md:left-6 z-10 max-w-md"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 mb-3">
            HVAC · Sheet Metal · Service · Since 1965
          </p>
          <p className="font-display text-2xl md:text-3xl leading-[0.95]">
            <LineReveal delay={1.6}>North Babylon, New York</LineReveal>
          </p>
        </motion.div>
      </section>
    </main>
  );
}
